
// ─── Indicator definitions ────────────────────────────────────────────────────

export const INDICATOR_GROUPS = {
    TREND: 'Trend',
    MOMENTUM: 'Momentum',
    VOLATILITY: 'Volatility',
};

/**
 * Master config for every supported indicator.
 * `params` drives the settings form automatically.
 * `seriesCount` tells the remove logic how many series to hide/show.
 */
export const INDICATORS = {
    EMA: {
        id: 'EMA',
        label: 'EMA',
        fullLabel: 'Exponential Moving Average',
        group: INDICATOR_GROUPS.TREND,
        seriesCount: 1,
        defaultParams: { period: 14, colour: '#18dae6', thickness: 1 },
        params: [
            { key: 'period', label: 'Period', type: 'number', min: 1, max: 200 },
            { key: 'colour', label: 'Color', type: 'color' },
            { key: 'thickness', label: 'Thickness', type: 'number', min: 1, max: 5 },
        ],
    },
    SMA: {
        id: 'SMA',
        label: 'SMA',
        fullLabel: 'Simple Moving Average',
        group: INDICATOR_GROUPS.TREND,
        seriesCount: 1,
        defaultParams: { period: 14, colour: '#da4931', thickness: 1 },
        params: [
            { key: 'period', label: 'Period', type: 'number', min: 1, max: 200 },
            { key: 'colour', label: 'Color', type: 'color' },
            { key: 'thickness', label: 'Thickness', type: 'number', min: 1, max: 5 },
        ],
    },
    BBANDS: {
        id: 'BBANDS',
        label: 'BOLL',
        fullLabel: 'Bollinger Bands',
        group: INDICATOR_GROUPS.TREND,
        seriesCount: 3,
        defaultParams: {
            period: 20, deviation: 2,
            uppercolor: '#5ff347', upperthickness: 1,
            middlecolor: '#18dae6', middlethickness: 1,
            lowercolor: '#c21b26', lowerthickness: 1,
        },
        params: [
            { key: 'period', label: 'Period', type: 'number', min: 1, max: 200 },
            { key: 'deviation', label: 'Std Dev', type: 'number', min: 0.1, max: 5, step: 0.1 },
            { key: 'uppercolor', label: 'Upper Color', type: 'color' },
            { key: 'upperthickness', label: 'Upper Width', type: 'number', min: 1, max: 5 },
            { key: 'middlecolor', label: 'Middle Color', type: 'color' },
            { key: 'middlethickness', label: 'Middle Width', type: 'number', min: 1, max: 5 },
            { key: 'lowercolor', label: 'Lower Color', type: 'color' },
            { key: 'lowerthickness', label: 'Lower Width', type: 'number', min: 1, max: 5 },
        ],
    },
    MACD: {
        id: 'MACD',
        label: 'MACD',
        fullLabel: 'MACD',
        group: INDICATOR_GROUPS.MOMENTUM,
        seriesCount: 2,
        defaultParams: {
            fastperiod: 12, slowPeriod: 26, signalPeriod: 9,
            thickness: 1, macd_colour: '#5ff347', signal_colour: '#18dae6',
        },
        params: [
            { key: 'fastperiod', label: 'Fast Period', type: 'number', min: 1, max: 100 },
            { key: 'slowPeriod', label: 'Slow Period', type: 'number', min: 1, max: 200 },
            { key: 'signalPeriod', label: 'Signal Period', type: 'number', min: 1, max: 100 },
            { key: 'macd_colour', label: 'MACD Color', type: 'color' },
            { key: 'signal_colour', label: 'Signal Color', type: 'color' },
            { key: 'thickness', label: 'Thickness', type: 'number', min: 1, max: 5 },
        ],
    },
    SO: {
        id: 'SO',
        label: 'KDJ',
        fullLabel: 'Stochastic Oscillator',
        group: INDICATOR_GROUPS.MOMENTUM,
        seriesCount: 2,
        defaultParams: {
            kPeriod: 14, dPeriod: 3, thickness: 1,
            overbuy: 80, overbuy_color: '#5ff347',
            oversell: 20, oversell_color: '#c21b26',
            kseries_color: '#d0e521', dseries_color: '#18dae6',
        },
        params: [
            { key: 'kPeriod', label: '%K Period', type: 'number', min: 1, max: 100 },
            { key: 'dPeriod', label: '%D Period', type: 'number', min: 1, max: 100 },
            { key: 'overbuy', label: 'Overbought', type: 'number', min: 50, max: 100 },
            { key: 'oversell', label: 'Oversold', type: 'number', min: 0, max: 50 },
            { key: 'kseries_color', label: '%K Color', type: 'color' },
            { key: 'dseries_color', label: '%D Color', type: 'color' },
            { key: 'thickness', label: 'Thickness', type: 'number', min: 1, max: 5 },
        ],
    },
    RSI: {
        id: 'RSI',
        label: 'RSI',
        fullLabel: 'Relative Strength Index',
        group: INDICATOR_GROUPS.MOMENTUM,
        seriesCount: 1,
        defaultParams: {
            period: 14, color: '#18dae6', thickness: 1,
            over: 70, over_color: '#5ff347',
            under: 30, under_color: '#c21b26',
        },
        params: [
            { key: 'period', label: 'Period', type: 'number', min: 1, max: 100 },
            { key: 'over', label: 'Overbought', type: 'number', min: 50, max: 100 },
            { key: 'under', label: 'Oversold', type: 'number', min: 0, max: 50 },
            { key: 'color', label: 'Line Color', type: 'color' },
            { key: 'thickness', label: 'Thickness', type: 'number', min: 1, max: 5 },
        ],
    },
    CCI: {
        id: 'CCI',
        label: 'CCI',
        fullLabel: 'Commodity Channel Index',
        group: INDICATOR_GROUPS.MOMENTUM,
        seriesCount: 1,
        defaultParams: { period: 14, color: '#5ff347', thickness: 1, color_trend: '#eda129' },
        params: [
            { key: 'period', label: 'Period', type: 'number', min: 1, max: 100 },
            { key: 'color', label: 'Line Color', type: 'color' },
            { key: 'color_trend', label: 'Level Color', type: 'color' },
            { key: 'thickness', label: 'Thickness', type: 'number', min: 1, max: 5 },
        ],
    },
    ROC: {
        id: 'ROC',
        label: 'ROC',
        fullLabel: 'Rate of Change',
        group: INDICATOR_GROUPS.MOMENTUM,
        seriesCount: 1,
        defaultParams: { period: 12, color: '#5ff347', thickness: 1 },
        params: [
            { key: 'period', label: 'Period', type: 'number', min: 1, max: 100 },
            { key: 'color', label: 'Line Color', type: 'color' },
            { key: 'thickness', label: 'Thickness', type: 'number', min: 1, max: 5 },
        ],
    },
    ADX: {
        id: 'ADX',
        label: 'ADX',
        fullLabel: 'Average Directional Index',
        group: INDICATOR_GROUPS.VOLATILITY,
        seriesCount: 3,
        defaultParams: {
            period: 14, thickness: 1,
            adxcolor: '#5ff347', mdicolor: '#da4931', pdicolor: '#c21b26',
        },
        params: [
            { key: 'period', label: 'Period', type: 'number', min: 1, max: 100 },
            { key: 'adxcolor', label: 'ADX Color', type: 'color' },
            { key: 'mdicolor', label: '-DI Color', type: 'color' },
            { key: 'pdicolor', label: '+DI Color', type: 'color' },
            { key: 'thickness', label: 'Thickness', type: 'number', min: 1, max: 5 },
        ],
    },
};

export const INDICATOR_LIST = Object.values(INDICATORS);
export const INDICATOR_IDS = Object.keys(INDICATORS);